import { useState, useRef, useEffect } from 'react';
import { read, utils } from 'xlsx';
import { getFileStats } from '../api';
import '../styles/components/dropzone.css';

export default function FileUpload({ file, onFileChange, mode, onFileStats }) {
  const [dragover, setDragover] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileStats, setFileStats] = useState(null);
  const inputRef = useRef();

  // Fetch processed count for this file from the DB
  useEffect(() => {
    if (!file) { setFileStats(null); return; }
    let cancelled = false;
    getFileStats(file.file.name, mode)
      .then(stats => {
        if (!cancelled) {
          setFileStats(stats);
          if (onFileStats) onFileStats(stats);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFileStats(null);
          if (onFileStats) onFileStats(null);
        }
      });
    return () => { cancelled = true; };
  }, [file, mode]);

  const LINKEDIN_HEADERS = ['linked url', 'linkedin url', 'linkedin link', 'linkedin'];

  const findLinkedInCol = (headers) => {
    for (let i = 0; i < headers.length; i++) {
      const h = (headers[i] || '').toString().trim().toLowerCase();
      if (LINKEDIN_HEADERS.includes(h) || h.includes('linkedin')) return i;
    }
    return -1;
  };

  const handleFile = (f) => {
    if (!f) return;
    setLoading(true);
    const reader = new FileReader();
    reader.onerror = () => {
      setLoading(false);
      alert('Failed to read file');
    };
    reader.onload = (e) => {
      try {
        const wb = read(e.target.result);
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (data.length <= 1) {
          onFileChange({ file: f, rowCount: 0, emptyLinkedIn: 0 });
          setLoading(false);
          return;
        }
        const headers = data[0];
        const rows = data.slice(1);
        const rowCount = rows.length;
        const liCol = findLinkedInCol(headers);
        const emptyLinkedIn = liCol >= 0
          ? rows.filter(r => !r[liCol] || !r[liCol].toString().trim()).length
          : null;
        onFileChange({ file: f, rowCount, emptyLinkedIn });
        setLoading(false);
      } catch (err) {
        setLoading(false);
        alert('Failed to parse file: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(f);
  };

  const remove = (e) => {
    e.stopPropagation();
    onFileChange(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const classes = ['dropzone'];
  if (dragover) classes.push('dragover');
  if (file) classes.push('has-file');

  return (
    <div
      className={classes.join(' ')}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragover(true); }}
      onDragLeave={() => setDragover(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragover(false);
        if (e.dataTransfer.files.length) handleFile(e.dataTransfer.files[0]);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files.length) handleFile(e.target.files[0]); }}
      />

      {loading ? (
        <div className="dropzone-text">Reading file...</div>
      ) : !file ? (
        <>
          <div className="dropzone-icon">&#128193;</div>
          <div className="dropzone-text">Drop your .xlsx or .csv file here</div>
          <div className="dropzone-hint">or click to browse</div>
        </>
      ) : (
        <div className="file-info">
          <div className="file-info-top">
            <span className="file-name">{file.file.name}</span>
            <span className="file-size">{(file.file.size / 1024).toFixed(1)} KB</span>
            <button className="file-remove" onClick={remove}>remove</button>
          </div>
          <div className="file-info-stats">
            <span>{file.rowCount.toLocaleString()} total rows</span>
            {mode === 'discovery' && file.emptyLinkedIn != null && (
              <>
                <span className="file-stat-sep">|</span>
                <span>{file.emptyLinkedIn.toLocaleString()} without LinkedIn</span>
                <span className="file-stat-skip">({(file.rowCount - file.emptyLinkedIn).toLocaleString()} already have LinkedIn)</span>
              </>
            )}
            {fileStats && fileStats.processed > 0 && (
              <>
                <span className="file-stat-sep">|</span>
                <span>{fileStats.processed.toLocaleString()} already processed</span>
                <span className="file-stat-sep">|</span>
                <span className="file-stat-highlight">
                  {(() => {
                    const base = mode === 'discovery' && file.emptyLinkedIn != null ? file.emptyLinkedIn : file.rowCount;
                    return Math.max(base - fileStats.processed, 0).toLocaleString();
                  })()} remaining
                </span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
